'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/components/theme/ThemeProvider';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';

export function ModeToggle() {
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { setMounted(true); }, []);

  const toggle = useCallback(() => {
    if (!resolvedTheme) return;
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';

    if (
      typeof document !== 'undefined' &&
      'startViewTransition' in document
    ) {
      const button = buttonRef.current;
      if (button) {
        const rect = button.getBoundingClientRect();
        const x = ((rect.left + rect.width / 2) / window.innerWidth) * 100;
        const y = ((rect.top + rect.height / 2) / window.innerHeight) * 100;
        document.documentElement.style.setProperty('--click-x', `${x}%`);
        document.documentElement.style.setProperty('--click-y', `${y}%`);
      } else {
        document.documentElement.style.setProperty('--click-x', '50%');
        document.documentElement.style.setProperty('--click-y', '50%');
      }
      (document as any).startViewTransition(() => setTheme(next));
    } else {
      setTheme(next);
    }
  }, [resolvedTheme, setTheme]);

  if (!mounted) return <div className="size-10" />;

  const isDark = resolvedTheme === 'dark';

  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      size="icon"
      onClick={toggle}
      className={cn(
        'relative z-50 size-10 rounded-full transition-all duration-300',
        'hover:bg-accent/20 focus-visible:ring-2 focus-visible:ring-primary',
        'active:scale-95'
      )}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={isDark ? 'dark' : 'light'}
          initial={{ y: 16, opacity: 0, rotate: -90, scale: 0.5 }}
          animate={{ y: 0, opacity: 1, rotate: 0, scale: 1 }}
          exit={{ y: -16, opacity: 0, rotate: 90, scale: 0.5 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className="flex items-center justify-center"
        >
          {isDark ? (
            <Moon className="size-[1.1rem] text-primary" />
          ) : (
            <Sun className="size-[1.1rem] text-foreground" />
          )}
        </motion.div>
      </AnimatePresence>
    </Button>
  );
}

export default ModeToggle;
